import { useCallback, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiTablePage from '../../components/common/ApiTablePage';
import ConfirmDeleteDialog from '../../components/common/ConfirmDeleteDialog';
import { API_CONFIG } from '../../config/api.config';
import useAPICall from '../../hooks/useAPICall';

const toolbarButtonSx = {
  ml: 2,
  px: 3,
  py: 1,
  fontWeight: 800,
  textTransform: 'none',
  borderRadius: '10px',
  backgroundColor: '#1E88E5',
  boxShadow: '0 4px 12px rgba(30, 136, 229, 0.2)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '& .MuiButton-startIcon': { mr: 1 },
  '&:hover': {
    backgroundColor: '#1565C0',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 16px rgba(30, 136, 229, 0.3)',
  },
  '&:active': { transform: 'translateY(0)' }
};

const COLUMN_WIDTHS = {
  compact: 100,
  standard: 160,
  medium: 140,
  large: 180,
  action: 96,
};

const getBusinessRuleCount = (row) => {
  if (!row?.computationTypes || typeof row.computationTypes !== 'object') {
    return 0;
  }

  const activeCount = Object.values(row.computationTypes)
    .filter((item) => item !== null && item !== undefined)
    .length;

  return activeCount > 1 ? activeCount + 14 : activeCount;
};

const isSystemGroup = (row) => (row?.groupName || row?.group_name || row?.name) === 'System';

const BusinessRules = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { execute } = useAPICall();
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, target: null });
  const entityType = location.pathname.includes('/configuration/drug/') ? 'drug' : 'hcp';
  const basePath = `/configuration/${entityType}`;

  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm.target) return;
    const groupName = deleteConfirm.target.groupName || deleteConfirm.target.group_name || deleteConfirm.target.name;
    if (!groupName) {
      setDeleteConfirm({ open: false, target: null });
      return;
    }

    try {
      await execute(
        { endpoint: '/rule/ruleConfig/businessRule/:groupName', method: 'DELETE' },
        null,
        {
          params: { groupName },
          successMessage: 'Business rule group deleted',
          showSuccessToast: true,
          errorMessage: 'Unable to delete business rule group.',
        }
      );
      setRefreshKey((prev) => prev + 1);
    } catch {
    } finally {
      setDeleteConfirm({ open: false, target: null });
    }
  }, [deleteConfirm.target, execute]);

  const columns = useMemo(() => ([
    { label: 'Name', keys: ['groupName', 'group_name', 'name'], size: COLUMN_WIDTHS.standard },
    { label: 'Description', keys: ['groupDescription', 'group_description', 'description'], size: COLUMN_WIDTHS.medium },
    { label: 'Default', keys: ['defaultGroup', 'default'], align: 'center', size: COLUMN_WIDTHS.compact },
    {
      label: 'Number of Rules',
      render: (row) => getBusinessRuleCount(row),
      align: 'center',
      size: COLUMN_WIDTHS.large,
    },
    {
      label: 'Action',
      align: 'center',
      size: COLUMN_WIDTHS.action,
      cell: ({ row }) => {
        const original = row.original;
        return (
          <>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`${basePath}/business-group/${original.groupName || original.name}`, { state: { row: original, mode: 'edit' } })}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ open: true, target: original })} disabled={isSystemGroup(original)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ]), [basePath, navigate]);

  return (
    <>
    <ApiTablePage
      title="Business Rules Group"
      subtitle={`${entityType.toUpperCase()} business rule groups`}
      apiConfig={API_CONFIG.config.getBusinessRules}
      initialPayload={{ entityType }}
      columns={columns}
      refreshKey={refreshKey}
      toolbarActions={(
        <Button className="action-btn process-btn"
          variant="contained"
          onClick={() => navigate(`${basePath}/business-group`)}
          sx={toolbarButtonSx}
        // startIcon={<AddIcon />}
        >
          Business Rules
        </Button>
      )}
      muiTableBodyRowProps={({ row }) => ({
        onDoubleClick: () => navigate(`${basePath}/business-group/${row.original.groupName || row.original.name}`, { state: { row: row.original, mode: 'edit' } }),
        sx: { cursor: 'pointer' },
      })}
    />
      <ConfirmDeleteDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, target: null })}
        onConfirm={confirmDelete}
        itemName="business rule group"
      />
    </>
  );
};

export default BusinessRules;
